import { useEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lightbulb, ArrowLeft, FolderPlus, Package, Layers, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { triggerCreditsGate } from "@/lib/credits-gate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { AngleCard, type AngleCardItem } from "./AngleCard";
import { AngleCopyDrawer, type AngleDetail } from "./AngleCopyDrawer";
import { CreateSetModal } from "./CreateSetModal";
import { BuildDestinationPicker } from "./BuildDestinationPicker";
import { dispatchAngleSetBuild } from "./angle-set-build";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import { useCanvasStore } from "@/pages/canvas/canvas-store";
import type { CanvasChatMessage, PlaybookProgressState } from "@/pages/canvas/canvas-types";
import type { AdAngleSet, Product } from "@shared/schema";

// COMPETITOR-stage cards carry a hydrated competitor brand name from the server.
type AngleDetailWithCompetitor = AngleDetail & { competitorName?: string };

interface AdAnglesPanelProps {
  brandId: number;
}

// Funnel-only stages — what a copy SET can contain. Sets never include
// COMPETITOR angles (those stay brand-level / legacy-only).
const FUNNEL_STAGES = ["TOF", "MOF", "BOF", "RETARGETING"] as const;
// All stages — used by the legacy (brand-level) view.
const ALL_STAGES = ["TOF", "MOF", "BOF", "RETARGETING", "COMPETITOR"] as const;
const STAGE_LABELS: Record<string, string> = {
  TOF: "Top of Funnel",
  MOF: "Middle of Funnel",
  BOF: "Bottom of Funnel",
  RETARGETING: "Retargeting",
  COMPETITOR: "Competitor",
};
const STAGE_SUBTITLES: Record<string, string> = {
  TOF: "Awareness · Curiosity",
  MOF: "Trust · Education",
  BOF: "Conversion · Urgency",
  RETARGETING: "Return · Recapture",
  COMPETITOR: "Adapted from competitor angles · grouped by source brand",
};

// ---------------------------------------------------------------------------
// View state machine — folder grid (default) → set detail (read) → legacy.
// ---------------------------------------------------------------------------
type PanelView = { level: "folders" } | { level: "set"; setId: number } | { level: "legacy" };

interface AngleSetsResponse {
  sets: AdAngleSet[];
  legacyCount: number;
}

interface AngleSetDetailResponse {
  set: AdAngleSet;
  angles: AngleDetail[];
}

// Brand-level ad-angles endpoint groups by funnel stage server-side.
interface AdAnglesByStageResponse {
  total: number;
  byStage: Record<string, AngleDetailWithCompetitor[]>;
}

function useProducts(brandId: number) {
  return useQuery<Product[]>({
    // Same key OverviewTab uses (canvas-brands-view.tsx) so the cache is shared.
    queryKey: ["/api/brands", brandId, "products"],
  });
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Keyboard activation for div/Card elements wired up as role="button". */
function handleActivateKeyDown(e: KeyboardEvent<HTMLElement>, activate: () => void) {
  // React synthetic keydowns from PORTALED children (kebab menu items, the
  // rename/delete dialogs) bubble through the REACT tree into this handler
  // even though those elements live elsewhere in the DOM. Without this guard,
  // typing a space in the rename input would suppress the character AND
  // "click" the card underneath.
  if (e.target !== e.currentTarget) return;
  if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
    e.preventDefault();
    activate();
  }
}

/**
 * Read an SSE response body to completion, invoking onEvent per parsed frame.
 * Frames are `event: <name>` + `data: <json>` blocks, blank-line delimited —
 * shared by the Build Campaign stream and the single-angle generate-ad drain.
 */
async function readSSEEvents(
  res: Response,
  onEvent: (eventName: string, data: any) => void,
): Promise<void> {
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventName = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) {
        try {
          onEvent(eventName, JSON.parse(data));
        } catch {
          onEvent(eventName, data);
        }
      }
    }
  }
}

/**
 * Renders the stage tabs + angle card grid + copy drawer for a set of angles.
 * Shared by both the set-detail view (funnel stages only) and the legacy view
 * (all 5 stages) so both share the exact same card/drawer behavior without
 * duplicating JSX.
 */
function StageGrid({
  angles,
  stages,
  onArchived,
  hideGenerateAction = false,
  onAddAngle,
  flat = false,
  singleColumn = false,
}: {
  angles: AngleDetail[];
  stages: readonly string[];
  onArchived: () => void;
  hideGenerateAction?: boolean;
  onAddAngle?: (stage: string) => void;
  /** v6.78 — no funnel classification (Dan: "just Angles, get rid of
   *  TOF/MOF/BOF/Retargeting"): one flat grid of every angle, no stage tabs.
   *  funnelStage survives only as card metadata. */
  flat?: boolean;
  /** v6.80 — chat Angles doc: one full-width card per row, untruncated,
   *  readable before opening (Dan: "1 card across the slide out"). */
  singleColumn?: boolean;
}) {
  const [activeStage, setActiveStage] = useState<string>(stages[0]);
  const [selected, setSelected] = useState<AngleDetail | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  const regeneratingRef = useRef(new Set<number>());
  const queryClient = useQueryClient();
  const regenerateAngle = async (angle: AngleDetail) => {
    if (regeneratingRef.current.has(angle.id)) return;
    regeneratingRef.current.add(angle.id);
    setRegeneratingIds(new Set(regeneratingRef.current));
    try {
      const response = await apiRequest('POST', `/api/ad-angles/${angle.id}/regenerate`);
      const updated: AngleDetail = await response.json();
      setSelected(current => current?.id === updated.id ? updated : current);
      await Promise.all(['angle-set', 'ad-angles', 'angle-sets', 'angle-detail'].map(key => queryClient.invalidateQueries({ queryKey: [key] })));
      onArchived();
      toast({ description: 'Concept regenerated. Generate an ad when you’re ready to use it.' });
    } catch (error) {
      toast({ description: error instanceof Error ? error.message : 'Could not regenerate this concept.', variant: 'destructive' });
    } finally {
      regeneratingRef.current.delete(angle.id);
      setRegeneratingIds(new Set(regeneratingRef.current));
    }
  };


  const archive = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/ad-angles/${id}`);
    },
    onSuccess: () => onArchived(),
  });

  const generateAdFromCard = useMutation({
    mutationFn: async ({ angleId, brandId }: { angleId: number; brandId: number }) => {
      const projectId = useCanvasStore.getState().activeProjectId;
      if (!projectId) {
        throw new Error("Open a canvas project first to generate ads onto it.");
      }
      const res = await fetch(`/api/brands/${brandId}/ad-angles/${angleId}/generate-ad`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      // Drain the SSE stream, watching for an `error` frame — the route
      // returns 200 + SSE even when generation fails, so an unconditional
      // success toast here would lie to the user.
      let streamError: string | null = null;
      await readSSEEvents(res, (eventName, data) => {
        if (eventName === "error") {
          streamError =
            (data as { message?: string })?.message ??
            (typeof data === "string" ? data : "Generation failed");
        }
      });
      if (streamError) throw new Error(streamError);
    },
    onSuccess: () => {
      toast({ description: "Ad generated on canvas — check your canvas project" });
    },
    onError: (err) => {
      toast({ description: `Failed: ${(err as Error).message}`, variant: "destructive" });
    },
  });

  const byStage = new Map<string, AngleDetail[]>();
  for (const s of stages) byStage.set(s, []);
  for (const a of angles) {
    // Callers already pre-filter archived angles; this is intentional
    // belt-and-braces so a future caller can't accidentally render them.
    if (a.status === "archived") continue;
    if (!byStage.has(a.funnelStage)) continue;
    byStage.get(a.funnelStage)!.push(a);
  }

  if (flat) {
    const flatAngles = angles.filter((a) => a.status !== "archived");
    return (
      <div className="space-y-3">
        {onAddAngle && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => onAddAngle("ANY")}
            data-testid="add-angle-flat"
          >
            <Plus className="h-3 w-3" /> Add angle
          </Button>
        )}
        <div className={singleColumn ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"}>
          {flatAngles.map((a) => (
            <AngleCard
              key={a.id}
              angle={a as AngleCardItem}
              onRegenerate={() => void regenerateAngle(a)}
              regenerating={regeneratingIds.has(a.id)}
              onClick={() => setSelected(a)}
              onArchive={() => archive.mutate(a.id)}
              onViewSource={() => setSelected(a)}
              onGenerateAd={() => generateAdFromCard.mutate({ angleId: a.id, brandId: a.brandId })}
              hideGenerateAction={hideGenerateAction}
              expanded={singleColumn}
            />
          ))}
          {flatAngles.length === 0 && (
            <div className="col-span-full text-sm text-muted-foreground py-8 text-center">
              No angles yet.
            </div>
          )}
        </div>
        <AngleCopyDrawer
          angle={selected}
          open={selected !== null}
          onOpenChange={(o) => {
            if (!o) setSelected(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeStage} onValueChange={setActiveStage}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
          {stages.map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>
              {/* v4.31 — count sits inline beside the title as a small pill
                  (was stacked underneath, which read as a stray number). */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium">{STAGE_LABELS[s]}</span>
                <span className="text-[10px] font-semibold tabular-nums leading-none rounded-full bg-muted-foreground/15 text-muted-foreground px-1.5 py-0.5">
                  {(byStage.get(s) ?? []).length}
                </span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {stages.map((s) => (
          <TabsContent key={s} value={s}>
            <p className="text-xs text-muted-foreground mb-3">{STAGE_SUBTITLES[s]}</p>
            {onAddAngle && s !== "COMPETITOR" && (
              <Button
                variant="outline"
                size="sm"
                className="mb-3 h-7 gap-1 text-xs"
                onClick={() => onAddAngle(s)}
                data-testid={`add-angle-${s}`}
              >
                <Plus className="h-3 w-3" /> Add angle
              </Button>
            )}
            {s === "COMPETITOR"
              ? (() => {
                  // Group COMPETITOR-stage angles by competitor brand name.
                  // Only reachable from the legacy view.
                  const items = (byStage.get("COMPETITOR") ?? []) as AngleDetailWithCompetitor[];
                  if (items.length === 0) {
                    return (
                      <div className="text-sm text-muted-foreground py-8 text-center">
                        No competitor angles yet.
                      </div>
                    );
                  }
                  const groups = new Map<string, AngleDetailWithCompetitor[]>();
                  for (const a of items) {
                    const key = a.competitorName ?? "Unknown competitor";
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key)!.push(a);
                  }
                  const entries = Array.from(groups.entries()).sort(([x], [y]) => x.localeCompare(y));
                  return (
                    <div className="space-y-6">
                      {entries.map(([name, group]) => (
                        <div key={name} className="space-y-2" data-testid={`competitor-group-${name}`}>
                          <div className="flex items-baseline gap-2 border-b pb-1">
                            <h3 className="text-sm font-semibold">{name}</h3>
                            <span className="text-xs text-muted-foreground">
                              {group.length} angle{group.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {group.map((a) => (
                              <AngleCard
                                key={a.id}
                                angle={a as AngleCardItem}
              onRegenerate={() => void regenerateAngle(a)}
              regenerating={regeneratingIds.has(a.id)}
                                onClick={() => setSelected(a)}
                                onArchive={() => archive.mutate(a.id)}
                                onViewSource={() => setSelected(a)}
                                onGenerateAd={() => generateAdFromCard.mutate({ angleId: a.id, brandId: a.brandId })}
                                hideGenerateAction={hideGenerateAction}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {(byStage.get(s) ?? []).map((a) => (
                    <AngleCard
                      key={a.id}
                      angle={a as AngleCardItem}
              onRegenerate={() => void regenerateAngle(a)}
              regenerating={regeneratingIds.has(a.id)}
                      onClick={() => setSelected(a)}
                      onArchive={() => archive.mutate(a.id)}
                      onViewSource={() => setSelected(a)}
                      onGenerateAd={() => generateAdFromCard.mutate({ angleId: a.id, brandId: a.brandId })}
                      hideGenerateAction={hideGenerateAction}
                    />
                  ))}
                  {(byStage.get(s) ?? []).length === 0 && (
                    <div className="col-span-full text-sm text-muted-foreground py-8 text-center">
                      No angles for this stage.
                    </div>
                  )}
                </div>
              )}
          </TabsContent>
        ))}
      </Tabs>

      <AngleCopyDrawer
        angle={selected}
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      />
    </div>
  );
}

function statusBadge(status: AdAngleSet["status"]) {
  if (status === "generating") {
    return <Badge variant="secondary" className="text-xs">Generating…</Badge>;
  }
  if (status === "failed") {
    return <Badge variant="destructive" className="text-xs">Failed</Badge>;
  }
  return null;
}

/** Small rename dialog — PATCH /api/angle-sets/:id { name }. */
function RenameSetDialog({
  set,
  brandId,
  onClose,
}: {
  set: AdAngleSet;
  brandId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(set.name);

  const rename = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/angle-sets/${set.id}`, { name: name.trim() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
      qc.invalidateQueries({ queryKey: ["angle-set", set.id] });
      onClose();
    },
    onError: (err) => {
      toast({ description: `Rename failed: ${(err as Error).message}`, variant: "destructive" });
    },
  });

  const canSave = name.trim().length > 0 && name.trim() !== set.name;

  return (
    <Dialog open onOpenChange={(o) => !o && !rename.isPending && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename set</DialogTitle>
          <DialogDescription>Give this copy set a new name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="rename-set-input">Name</Label>
          <Input
            id="rename-set-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            disabled={rename.isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSave) rename.mutate();
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={rename.isPending}>
            Cancel
          </Button>
          <Button onClick={() => rename.mutate()} disabled={!canSave || rename.isPending}>
            {rename.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Kebab menu for a folder card — Rename + Delete. The kebab only appears on
 * FolderGrid cards (not inside an open SetDetail), so there is no
 * "currently-open set" race to guard here. If a set the user has open
 * elsewhere (another tab) gets deleted, SetDetail's next fetch 404s and it
 * renders its "Set not found." state with the manual back link.
 */
function SetCardKebab({
  set,
  brandId,
}: {
  set: AdAngleSet;
  brandId: number;
}) {
  const qc = useQueryClient();
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteSet = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/angle-sets/${set.id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
      setDeleteOpen(false);
    },
    onError: (err) => {
      toast({ description: `Delete failed: ${(err as Error).message}`, variant: "destructive" });
    },
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" data-testid={`set-kebab-${set.id}`}>
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onSelect={() => setRenameOpen(true)} data-testid={`rename-set-${set.id}`}>
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setDeleteOpen(true)}
            className="text-destructive"
            data-testid={`delete-set-kebab-${set.id}`}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {renameOpen && (
        <RenameSetDialog set={set} brandId={brandId} onClose={() => setRenameOpen(false)} />
      )}

      <AlertDialog open={deleteOpen} onOpenChange={(o) => !o && !deleteSet.isPending && setDeleteOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this set?</AlertDialogTitle>
            <AlertDialogDescription>Its angles will be archived.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSet.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteSet.mutate();
              }}
              disabled={deleteSet.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSet.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function FolderGrid({
  brandId,
  onOpenSet,
  onOpenLegacy,
}: {
  brandId: number;
  onOpenSet: (setId: number) => void;
  onOpenLegacy: () => void;
}) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  // Failed-set retry: opens CreateSetModal pre-filled from the failed set
  // (product locked, direction carried over) — creates a FRESH set.
  const [retrySet, setRetrySet] = useState<AdAngleSet | null>(null);
  const { data: products = [] } = useProducts(brandId);

  const { data, isLoading, isError, refetch } = useQuery<AngleSetsResponse>({
    queryKey: ["angle-sets", brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/angle-sets`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load copy sets");
      return res.json();
    },
    // Generation runs for minutes server-side; keep 'generating' cards live
    // even after a mid-run page refresh.
    refetchInterval: (q) =>
      q.state.data?.sets?.some((s) => s.status === "generating") ? 5000 : false,
  });

  const deleteSet = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/angle-sets/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["angle-sets", brandId] }),
    onError: (err) => {
      toast({ description: `Delete failed: ${(err as Error).message}`, variant: "destructive" });
    },
  });

  const productsById = new Map(products.map((p) => [p.id, p]));

  // Hoisted above the early returns — both the empty-state and the populated
  // grid used to render their own copy of this JSX.
  const createModal = createOpen && (
    <CreateSetModal
      brandId={brandId}
      products={products}
      onClose={() => {
        setCreateOpen(false);
        // "Continue in background" contract: the cache must learn the new
        // 'generating' set NOW or the folder poll never engages (queryClient
        // uses staleTime: Infinity, so without this the set only appears
        // after a full page reload).
        qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
      }}
      onCreated={(set) => {
        setCreateOpen(false);
        qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
        onOpenSet(set.id);
      }}
    />
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
      </div>
    );
  }

  // Distinct from the empty state — a load failure must NOT invite creating a
  // duplicate set.
  if (isError && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <p className="text-sm text-muted-foreground">Couldn't load copy sets.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="retry-load-sets">
          Retry
        </Button>
      </div>
    );
  }

  const sets = data?.sets ?? [];
  const legacyCount = data?.legacyCount ?? 0;

  if (sets.length === 0 && legacyCount === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <Lightbulb className="h-12 w-12 text-muted-foreground" />
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">No copy sets yet</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Create your first one — a set of TOF/MOF/BOF/retargeting ad angles for a single product.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="create-set-button">
            <FolderPlus className="h-3.5 w-3.5 mr-2" />
            Create new set
          </Button>
        </div>
        {createModal}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Copy Sets</h2>
          <p className="text-xs text-muted-foreground">Folders of ad angles, one per product / direction</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Card
          onClick={() => setCreateOpen(true)}
          className="cursor-pointer hover:shadow-md transition-shadow p-4 flex flex-col items-center justify-center gap-2 h-36 border-dashed text-muted-foreground hover:text-foreground"
          data-testid="create-set-card"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => handleActivateKeyDown(e, () => setCreateOpen(true))}
        >
          <FolderPlus className="h-6 w-6" />
          <span className="text-sm font-medium">Create new set</span>
        </Card>

        {sets.map((set) => {
          const product = productsById.get(set.productId);
          return (
            <Card
              key={set.id}
              onClick={() => onOpenSet(set.id)}
              className="cursor-pointer hover:shadow-md transition-shadow p-4 flex flex-col gap-2 min-h-36"
              data-testid={`set-card-${set.id}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => handleActivateKeyDown(e, () => onOpenSet(set.id))}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm truncate flex-1" title={set.name}>
                  {set.name}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {statusBadge(set.status)}
                  {set.status !== "failed" && (
                    <SetCardKebab set={set} brandId={brandId} />
                  )}
                </div>
              </div>
              {product && (
                <Badge variant="outline" className="text-xs w-fit gap-1">
                  <Package className="h-3 w-3" />
                  {product.name}
                </Badge>
              )}
              {set.direction && (
                <p className="text-xs text-muted-foreground line-clamp-1">{set.direction}</p>
              )}
              <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                <span>{set.angleCount} angle{set.angleCount === 1 ? "" : "s"}</span>
                <span>{formatDate(set.createdAt)}</span>
              </div>
              {set.status === "failed" && (
                <div className="flex items-center gap-2 pt-1 border-t">
                  <p className="text-xs text-destructive flex-1">Generation failed</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRetrySet(set);
                    }}
                    data-testid={`retry-set-${set.id}`}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSet.mutate(set.id);
                    }}
                    disabled={deleteSet.isPending}
                    data-testid={`delete-set-${set.id}`}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </Card>
          );
        })}

        {legacyCount > 0 && (
          <Card
            onClick={onOpenLegacy}
            className="cursor-pointer hover:shadow-md transition-shadow p-4 flex flex-col gap-2 h-36 bg-muted/30"
            data-testid="legacy-card"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleActivateKeyDown(e, onOpenLegacy)}
          >
            <div className="flex items-start gap-2">
              <Layers className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div className="font-medium text-sm">Legacy — brand-level angles</div>
            </div>
            <p className="text-xs text-muted-foreground mt-auto">
              {legacyCount} brand-level angle{legacyCount === 1 ? "" : "s"} · view only
            </p>
          </Card>
        )}
      </div>

      {createModal}

      {retrySet && (
        <CreateSetModal
          brandId={brandId}
          products={products}
          initialProductId={retrySet.productId}
          initialDirection={retrySet.direction ?? undefined}
          onClose={() => {
            setRetrySet(null);
            // Same continue-in-background contract as the create modal above.
            qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
          }}
          onCreated={(set) => {
            setRetrySet(null);
            qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
            onOpenSet(set.id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build Campaign — SSE stream from POST /api/angle-sets/:id/build.
// Frames are FLATTENED `{type, ...fields}` on the wire — key on the `event:`
// line, same pattern as CreateSetModal's streamCreateSet. Do NOT parse this
// as canvas-chat's `{type, data}` envelope.
// ---------------------------------------------------------------------------
interface BuildCompletedResult {
  projectId: number;
  workflowRunId: number;
  generated: number;
  failed: number;
  skippedStages: string[];
}

// NOTE: intentionally no AbortSignal wiring here — Build Campaign is meant to
// survive the user navigating away (server-side run keeps going; see
// handleBuild's caller). Unlike CreateSetModal's create-set stream, there is
// no unmount-abort requirement for this one.
async function streamBuildCampaign(
  setId: number,
  body: { projectId?: number; stage?: string },
  onEvent: (eventName: string, data: any) => void,
): Promise<void> {
  const res = await fetch(`/api/angle-sets/${setId}/build`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Pre-SSE failure — server sends plain JSON, not an event stream.
    let message = `Request failed (HTTP ${res.status})`;
    try {
      const data = await res.json();
      if (data?.code === "CREDITS_EXHAUSTED") {
        // Raise the app-wide out-of-credits → upgrade-pricing modal (same
        // gate the canvas uses) in addition to the inline error.
        triggerCreditsGate(Number(data.balance ?? 0), Number(data.needed ?? 0));
        message = "Not enough credits to build this campaign.";
      } else if (data?.error === "no_active_angles") {
        message = data?.message || "This set has no active angles — unarchive or create some first.";
      } else if (typeof data?.message === "string") {
        message = data.message;
      } else if (typeof data?.error === "string") {
        message = data.error;
      }
    } catch {
      // ignore — fall back to generic message
    }
    throw new Error(message);
  }

  await readSSEEvents(res, onEvent);
}

// ---------------------------------------------------------------------------
// Client-side duplicate-build guard. A build outlives its SetDetail mount
// (user can navigate away and come back), so in-flight setIds live at module
// scope and components subscribe via useSyncExternalStore — a remounted
// SetDetail sees the button disabled and flips back when the stream settles.
// Server-side locking is a later follow-up.
// ---------------------------------------------------------------------------
const buildingSetIds = new Set<number>();
const buildListeners = new Set<() => void>();
function notifyBuildListeners() {
  // .forEach, not for..of — this tsconfig target can't iterate Sets directly.
  buildListeners.forEach((l) => l());
}
function subscribeToBuilds(cb: () => void) {
  buildListeners.add(cb);
  return () => {
    buildListeners.delete(cb);
  };
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      data-testid="back-to-folders"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Copy sets
    </button>
  );
}

// v6.78 — exported: the chat Angles doc (canvas AngleSetDoc sidebar) mounts
// this SAME view, so chat-created sets get the identical editable sheet as
// the Brands tab (Dan: "we already have working sheets that are EDITABLE —
// not this new one"). One surface, one behavior.
export function SetDetail({
  brandId,
  setId,
  onBack,
  docMode = false,
  originProjectId,
  onBuildStarted,
}: {
  brandId: number;
  setId: number;
  onBack: () => void;
  /** v6.80 — rendered inside the chat Angles doc sidebar: full-width
   *  single-column cards, untruncated. */
  docMode?: boolean;
  originProjectId?: number | null;
  /** Runs after the document's owning task accepts the build dispatch. */
  onBuildStarted?: () => void;
}) {
  const qc = useQueryClient();
  const { data: products = [] } = useProducts(brandId);
  const [, navigate] = useLocation();

  // `null` data = genuine 404 (set deleted); thrown error = transient failure
  // (network/500) and gets a Retry instead of a misleading "not found".
  const { data, isLoading, isError, refetch } = useQuery<AngleSetDetailResponse | null>({
    queryKey: ["angle-set", setId],
    queryFn: async () => {
      const res = await fetch(`/api/angle-sets/${setId}`, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to load set");
      return res.json();
    },
    // Keep a mid-generation set live if the user navigates in while it runs.
    refetchInterval: (q) => (q.state.data?.set?.status === "generating" ? 5000 : false),
  });

  // Build Campaign state. `building` is derived from the module-scope
  // in-flight set (not local state) so a user returning to a mid-build set
  // sees the button disabled, and it re-enables when the stream settles.
  const building = useSyncExternalStore(subscribeToBuilds, () => buildingSetIds.has(setId));
  // v7.25 — concepts-first auto-builds mark the set in the canvas store
  // (angle_set_card building:true); the doc panel's Build button stands down
  // instead of inviting a duplicate run mid-build.
  const chatBuildActive = useCanvasStore((s) => (s.angleSetBuildingUntil[setId] ?? 0) > Date.now());
  const [buildProgress, setBuildProgress] = useState<{ done: number; total: number } | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [showDestPicker, setShowDestPicker] = useState(false);
  // Which funnel stage a per-level Build button targeted (null = full funnel).
  // The BuildDestinationPicker runs between the click and the actual build, so
  // we stash the chosen stage here and read it back in onPicked. (Feature #2)
  const [pendingStage, setPendingStage] = useState<string | null>(null);

  // "+" add-card (feature #3): generate ONE angle for a stage from a prompt.
  const [addAngleStage, setAddAngleStage] = useState<string | null>(null);
  const [addPrompt, setAddPrompt] = useState("");
  const addAngle = useMutation({
    mutationFn: async ({ stage, prompt }: { stage: string; prompt: string }) => {
      const res = await fetch(`/api/angle-sets/${setId}/angles/generate-one`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage, prompt }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        if (d?.code === "CREDITS_EXHAUSTED") {
          triggerCreditsGate(Number(d.balance ?? 0), Number(d.needed ?? 0));
          throw new Error("Not enough credits to add an angle.");
        }
        throw new Error(d?.message || `Request failed (HTTP ${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["angle-set", setId] });
      qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
      toast({ description: "Angle added to the set." });
      setAddAngleStage(null);
      setAddPrompt("");
    },
    onError: (err) => toast({ description: (err as Error).message, variant: "destructive" }),
  });

  // The build is explicitly allowed to keep running server-side after the
  // user navigates away from this set (Part A.3). Guard local state updates
  // with a mounted flag so a build that outlives this component doesn't call
  // setState on an unmounted SetDetail. Toasts are NOT guarded — they render
  // globally, and `project_resolved` typically unmounts this component (the
  // canvas navigation), so a guarded toast would silently drop the outcome.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // `projectId` always comes from the BuildDestinationPicker — an explicit
  // user choice. Never fall back to activeProjectId here: that's how a second
  // build silently dumped 17 more ads into the previous build's canvas.
  async function handleBuild(projectId: number, buildStage?: string | null) {
    if (!data || buildingSetIds.has(setId)) return;
    const activeAngles = data.angles
      .filter((a) => a.status !== "archived")
      .filter((a) => !buildStage || a.funnelStage === buildStage);
    const buildLabel = buildStage
      ? `${STAGE_LABELS[buildStage] ?? buildStage} Campaign`
      : "Full Funnel Campaign";
    setBuildError(null);
    setBuildProgress({ done: 0, total: activeAngles.length });
    buildingSetIds.add(setId);
    notifyBuildListeners();

    // Assigned inside try (needs the stream's closure state); called from
    // both the success/error tails and the catch.
    let finalizeCard: (summary: string, failed: boolean) => void = () => {};

    try {
      let completed: BuildCompletedResult | null = null;
      let streamError: string | null = null;
      let doneCount = 0;
      let resolvedProjectId: number | null = null;
      let lastTileReload = 0;

      // ── In-chat progress card ─────────────────────────────────────────
      // The same PlaybookTaskCard other playbooks show, driven by this
      // stream's stage/image events. Card state lives in this closure and is
      // UPSERTED into the store after every change AND after every
      // reloadProject — reloadProject replaces the message list from the DB,
      // which would otherwise wipe the live card.
      const setName = data.set.name;
      const cardStages = FUNNEL_STAGES.filter((s) =>
        activeAngles.some((a) => a.funnelStage === s),
      );
      const userMsgId = `m-ffc-user-${Date.now()}`;
      const cardMsgId = `m-ffc-${Date.now()}`;
      let cardCreated = false;
      const cardProgress: PlaybookProgressState = {
        playbookName: buildLabel,
        totalPhases: cardStages.length,
        phases: cardStages.map((s) => ({
          id: s,
          name: STAGE_LABELS[s] ?? s,
          phaseType: "generation",
        })),
        phaseStatuses: Object.fromEntries(cardStages.map((s) => [s, "pending" as const])),
        phaseStatusTexts: {},
        activePhaseId: null,
        qaResults: [],
        conceptProgress: { current: 0, total: activeAngles.length },
        completedSummary: null,
        isDone: false,
      };
      let cardContent = `Building ${buildLabel}…`;
      let cardStreaming = true;
      const syncCard = () => {
        if (resolvedProjectId == null || !cardCreated) return;
        const userMsg: CanvasChatMessage = {
          id: userMsgId,
          role: "user",
          content: `Build ${buildLabel} — ${setName}`,
          timestamp: Date.now(),
        };
        const cardMsg: CanvasChatMessage = {
          id: cardMsgId,
          role: "assistant",
          content: cardContent,
          isStreaming: cardStreaming,
          timestamp: Date.now(),
          playbookProgress: {
            ...cardProgress,
            phaseStatuses: { ...cardProgress.phaseStatuses },
            phaseStatusTexts: { ...cardProgress.phaseStatusTexts },
            conceptProgress: cardProgress.conceptProgress ? { ...cardProgress.conceptProgress } : null,
          },
        };
        useCanvasStore.getState()._writeMessages(resolvedProjectId, (msgs) => {
          let next = msgs.some((m) => m.id === userMsgId)
            ? msgs
            : [...msgs, userMsg];
          next = next.some((m) => m.id === cardMsgId)
            ? next.map((m) => (m.id === cardMsgId ? cardMsg : m))
            : [...next, cardMsg];
          return next;
        });
      };

      await streamBuildCampaign(
        setId,
        { projectId, stage: buildStage ?? undefined },
        (eventName, evtData) => {
          if (eventName === "project_resolved") {
            const targetProjectId = (evtData as { projectId?: number })?.projectId;
            if (typeof targetProjectId === "number") {
              resolvedProjectId = targetProjectId;
              const store = useCanvasStore.getState();
              store.switchProject(targetProjectId, { brandId })
                .then(async () => {
                  // A fresh build project has no loaded images yet, so
                  // switchProject's auto-open never fires and the user lands
                  // in the empty CHAT pane while ads build. Force the canvas
                  // open (same patch the intent_resolved handler applies).
                  useCanvasStore.getState()._writeProjectView(targetProjectId, {
                    isCanvasOpen: true,
                    activeView: "chat-canvas" as const,
                  });
                  await useCanvasStore.getState().reloadProject(targetProjectId).catch(() => {});
                  // Card goes in only AFTER the conversation has loaded —
                  // switchProject/reloadProject REPLACE the message list.
                  cardCreated = true;
                  syncCard();
                })
                .catch(() => {});
              if (!window.location.pathname.startsWith("/canvas")) {
                navigate("/canvas");
              }
            }
          } else if (eventName === "canvas_image_ready" || eventName === "canvas_image_failed") {
            doneCount += 1;
            if (mountedRef.current) {
              setBuildProgress((prev) => ({ done: doneCount, total: prev?.total ?? activeAngles.length }));
            }
            const stage = (evtData as { stage?: string })?.stage;
            if (stage && cardProgress.phaseStatuses[stage] === "pending") {
              cardProgress.phaseStatuses[stage] = "running";
              cardProgress.activePhaseId = stage;
            }
            cardProgress.conceptProgress = { current: doneCount, total: activeAngles.length };
            syncCard();
            // This SSE stream is detached from the canvas store, so tiles only
            // appear when the project's images refresh. Non-destructive
            // refresh (throttled) so the canvas fills in live — reloadProject
            // here blanked the canvas for the whole refetch round-trip and
            // wiped the chat card (both user-visible on slow links).
            if (resolvedProjectId != null && Date.now() - lastTileReload > 4000) {
              lastTileReload = Date.now();
              useCanvasStore.getState().refreshProjectImages(resolvedProjectId).catch(() => {});
            }
          } else if (eventName === "stage_completed") {
            const stage = (evtData as { stage?: string })?.stage;
            if (stage && cardProgress.phaseStatuses[stage]) {
              cardProgress.phaseStatuses[stage] = "completed";
              if (cardProgress.activePhaseId === stage) cardProgress.activePhaseId = null;
              syncCard();
            }
          } else if (eventName === "completed") {
            completed = evtData as BuildCompletedResult;
          } else if (eventName === "error") {
            streamError = (evtData as { message?: string })?.message ?? "Build failed";
          }
          // `playbook_completed` is intentionally ignored — `completed` (the
          // route-level frame) is the terminal signal.
        },
      );

      // Finalize the chat card for either outcome, then persist the
      // conversation so the card survives reloads and project switches.
      finalizeCard = (summary: string, failed: boolean) => {
        if (!cardCreated || resolvedProjectId == null) return;
        for (const s of cardStages) {
          if (cardProgress.phaseStatuses[s] !== "completed") {
            cardProgress.phaseStatuses[s] = failed ? "failed" : "completed";
          }
        }
        cardProgress.activePhaseId = null;
        cardProgress.isDone = true;
        cardProgress.completedSummary = summary;
        cardContent = summary;
        cardStreaming = false;
        syncCard();
        useCanvasStore.getState().saveConversationHistory(resolvedProjectId).catch(() => {});
      };

      if (streamError) {
        finalizeCard(`Build failed: ${streamError}`, true);
        toast({ description: `Build failed: ${streamError}`, variant: "destructive" });
        if (mountedRef.current) setBuildError(streamError);
        return;
      }
      if (completed) {
        const result = completed as BuildCompletedResult;
        const summary =
          `Full Funnel Campaign complete — ${result.generated} ads generated` +
          (result.failed > 0 ? `, ${result.failed} failed` : "") + ".";
        finalizeCard(summary, false);
        toast({
          description: `Full Funnel Campaign built — ${result.generated} ads (${result.failed} failed)`,
        });
        // Final tile refresh — non-destructive, so the conversation (and the
        // just-finalized card) is never replaced. The old full reloadProject
        // here raced finalizeCard's conversation save and blinked the
        // finished card away.
        useCanvasStore.getState().refreshProjectImages(result.projectId).catch(() => {});
        if (mountedRef.current) setBuildProgress(null);
        return;
      }
      finalizeCard("Build ended unexpectedly.", true);
      toast({ description: "Build ended unexpectedly. Please try again.", variant: "destructive" });
      if (mountedRef.current) setBuildError("Build ended unexpectedly. Please try again.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // A dropped stream is NOT a failed build (server keeps going), but the
      // card can't update anymore — close it out honestly.
      finalizeCard(`Lost connection to the build stream — the build continues server-side. ${msg}`, true);
      toast({ description: `Build failed: ${msg}`, variant: "destructive" });
      if (mountedRef.current) setBuildError(msg);
    } finally {
      buildingSetIds.delete(setId);
      notifyBuildListeners();
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
          <p className="text-sm text-muted-foreground">Couldn't load this set.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="retry-load-set">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <p className="text-sm text-muted-foreground py-8 text-center">Set not found.</p>
      </div>
    );
  }

  const { set, angles } = data;
  const product = products.find((p) => p.id === set.productId);
  const activeAngles = angles.filter((a) => a.status !== "archived");

  return (
    <div className="space-y-4">
      <BackLink onBack={onBack} />

      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <h2 className="text-lg font-semibold truncate">{set.name}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {product && (
              <Badge variant="outline" className="text-xs gap-1">
                <Package className="h-3 w-3" />
                {product.name}
              </Badge>
            )}
            {statusBadge(set.status)}
          </div>
          {set.direction && (
            <p className="text-xs text-muted-foreground">{set.direction}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Button
            size="sm"
            onClick={() => {
              setPendingStage(null);
              // v6.81/v6.84 — from the chat Angles doc the user is ALREADY in
              // a canvas: build straight into it, no destination picker — and
              // through the FLAT anchored angles renderer (a set-scoped chat
              // message), NOT the funnel-staged Full Funnel playbook (Dan:
              // "these are just angles" — no TOF/MOF/BOF in chat or canvas).
              // The Brands tab keeps the picker + Full Funnel build.
              if (docMode) {
                const store = useCanvasStore.getState();
                let dispatched = false;
                void dispatchAngleSetBuild({ setId, brandId, originProjectId, activeProjectId: store.activeProjectId, activeBrandId: store.activeBrandId, set, angles }, (message, preMsgId, context) => {
                  dispatched = true;
                  // v7.25/28 — mark the set building in BOTH stores: the
                  // module-scope buildingSetIds (this chat path never set it
                  // — only the Brands-tab REST build did — so a second click
                  // double-built the set; timer-cleared, the server in-flight
                  // guard is the hard stop) AND the canvas store map, which
                  // the AUTO-build path (angle_set_card building:true) also
                  // writes so the button stands down with zero clicks.
                  buildingSetIds.add(setId);
                  notifyBuildListeners();
                  setTimeout(() => { buildingSetIds.delete(setId); notifyBuildListeners(); }, 3 * 60_000);
                  useCanvasStore.setState((s) => ({ angleSetBuildingUntil: { ...s.angleSetBuildingUntil, [setId]: Date.now() + 6 * 60_000 } }));
                  const build = store.sendAgentMessage(message, preMsgId, context);
                  onBuildStarted?.();
                  return build;
                }).catch((error) => {
                  if (dispatched) {
                    buildingSetIds.delete(setId);
                    notifyBuildListeners();
                    useCanvasStore.setState((s) => ({ angleSetBuildingUntil: { ...s.angleSetBuildingUntil, [setId]: 0 } }));
                  }
                  toast({ description: error instanceof Error ? error.message : 'Could not start this build.', variant: 'destructive' });
                });
                return;
              }
              setShowDestPicker(true);
            }}
            disabled={building || chatBuildActive || activeAngles.length === 0}
            data-testid="build-campaign-button"
          >
            {building || chatBuildActive ? "Building…" : "Build Campaign"}
          </Button>
          {/* v6.78 — per-stage build segmented control removed with the
              funnel classification (Dan: "just Angles"). One Build Campaign
              button builds the whole set. */}
          {building && buildProgress && (
            <span className="text-xs text-muted-foreground">
              Building — {buildProgress.done} of {buildProgress.total} ads done
            </span>
          )}
        </div>
      </div>

      {buildError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {buildError}
        </div>
      )}

      {showDestPicker && (
        <BuildDestinationPicker
          brandId={brandId}
          setName={set.name}
          adCount={activeAngles.length}
          onPicked={(projectId) => {
            setShowDestPicker(false);
            void handleBuild(projectId, pendingStage);
            setPendingStage(null);
          }}
          onClose={() => { setShowDestPicker(false); setPendingStage(null); }}
        />
      )}

      {/* "+" add-card modal (feature #3): generate one angle from a prompt. */}
      <Dialog
        open={addAngleStage !== null}
        onOpenChange={(o) => { if (!o && !addAngle.isPending) { setAddAngleStage(null); setAddPrompt(""); } }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{addAngleStage === "ANY" ? "Add an angle" : `Add a ${addAngleStage} angle`}</DialogTitle>
            <DialogDescription>
              {addAngleStage === "ANY"
                ? "Describe the angle you want — we'll generate one card for this set."
                : `Describe the angle you want — we'll generate one card for the ${addAngleStage} stage.`}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={addPrompt}
            onChange={(e) => setAddPrompt(e.target.value)}
            placeholder="e.g. an urgency angle about a 24-hour flash sale, bold and edgy"
            rows={3}
            maxLength={500}
            autoFocus
            data-testid="add-angle-prompt"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setAddAngleStage(null); setAddPrompt(""); }}
              disabled={addAngle.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => addAngleStage && addAngle.mutate({ stage: addAngleStage, prompt: addPrompt })}
              disabled={addAngle.isPending || addPrompt.trim().length < 3}
              data-testid="add-angle-generate"
            >
              {addAngle.isPending ? "Generating…" : "Generate angle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StageGrid
        angles={activeAngles}
        stages={FUNNEL_STAGES}
        flat
        singleColumn={docMode}
        onAddAngle={(stage) => { setAddPrompt(""); setAddAngleStage(stage); }}
        onArchived={() => {
          // Both views cache counts: the set detail AND the folder card's
          // angleCount (kept honest server-side by decrementAngleSetCount).
          qc.invalidateQueries({ queryKey: ["angle-set", setId] });
          qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
        }}
      />
    </div>
  );
}

/**
 * Legacy view — brand-level (set_id IS NULL) angles, rendered with the full
 * 5-stage StageGrid (ALL_STAGES). View-only: the kebab's "Generate ad" item
 * is hidden via AngleCard's hideGenerateAction; Archive stays enabled.
 */
function LegacyView({ brandId, onBack }: { brandId: number; onBack: () => void }) {
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<AdAnglesByStageResponse>({
    queryKey: ["ad-angles", brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/ad-angles`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load legacy angles");
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
          <p className="text-sm text-muted-foreground">Couldn't load legacy angles.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="retry-load-legacy">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Flatten byStage back into a single list, filtered to brand-level
  // (setId == null) and not-archived — set-scoped angles have their own
  // dedicated views (folders → set detail).
  const allAngles: AngleDetailWithCompetitor[] = Object.values(data?.byStage ?? {}).flat();
  const legacyAngles = allAngles.filter(
    (a) => (a as unknown as { setId?: number | null }).setId == null && a.status !== "archived",
  );

  return (
    <div className="space-y-4">
      <BackLink onBack={onBack} />
      <div>
        <h2 className="text-lg font-semibold">Legacy — brand-level angles</h2>
        <p className="text-xs text-muted-foreground">
          {legacyAngles.length} angle{legacyAngles.length === 1 ? "" : "s"} · view only
        </p>
      </div>

      <StageGrid
        angles={legacyAngles}
        stages={ALL_STAGES}
        hideGenerateAction
        onArchived={() => {
          // legacyCount lives in the angle-sets folder response too.
          qc.invalidateQueries({ queryKey: ["ad-angles", brandId] });
          qc.invalidateQueries({ queryKey: ["angle-sets", brandId] });
        }}
      />
    </div>
  );
}

export function AdAnglesPanel({ brandId }: AdAnglesPanelProps) {
  const [view, setView] = useState<PanelView>({ level: "folders" });

  if (view.level === "set") {
    return (
      <SetDetail
        brandId={brandId}
        setId={view.setId}
        onBack={() => setView({ level: "folders" })}
      />
    );
  }

  if (view.level === "legacy") {
    return <LegacyView brandId={brandId} onBack={() => setView({ level: "folders" })} />;
  }

  return (
    <FolderGrid
      brandId={brandId}
      onOpenSet={(setId) => setView({ level: "set", setId })}
      onOpenLegacy={() => setView({ level: "legacy" })}
    />
  );
}
